import { useAtom } from 'jotai';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IAnalysisResult } from 'src/presentation/types/interfaces/IAnalysisResult.interface';

import { Flex } from '@/lib/v2/components/Layout/Stack';
import { Text } from '@/lib/v2/components/Text';
import { StatsCards, StatsProps } from '@/lib/v2/examples/StatsCards/StatsCards';
import { CheckSimple } from '@/lib/v2/icons/solid';

import { quantifier } from '@/src/ContactsModule/utils/quantifier';

import { ProgressCard, ProgressCardProps } from './ProgressCard';

import { atomStep3ShowProgressDerived } from '@/modules/ContactsModule/atoms/Step3';
import { atomShowProgressNotification } from '@/modules/ContactsModule/atoms/Steps';

export const ContactsViewInfo = memo(
  ({
    analysisResult,
    dataCard,
  }: {
    analysisResult: IAnalysisResult | undefined;
    dataCard?: any;
  }) => {
    const [showProgressCard] = useAtom(atomStep3ShowProgressDerived);
    const [showProgressNotification, setShowProgressNotification] = useAtom(
      atomShowProgressNotification
    );
    const { t } = useTranslation();

    const progressCard: ProgressCardProps = useMemo(
      () => ({
        title: t('IMPORT_MAIN.importingList'),
        dataCard: dataCard,
        setHiddenProgressCard: () => {
          analysisResult &&
            setShowProgressNotification((prevProgressNotification) => ({
              ...prevProgressNotification,
              show: false,
            }));
        },
        status: showProgressNotification.status,
        step: showProgressNotification.step,
        percentage: showProgressNotification.percentage,
        mode: showProgressNotification.velocity,
      }),
      [analysisResult, dataCard, setShowProgressNotification, showProgressNotification, t]
    );

    const importState = useMemo(
      () =>
        [
          {
            quantity: quantifier(analysisResult?.detectedContacts),
            state: t('IMPORT_MAIN.processed'),
          },
          {
            quantity:
              analysisResult?.importedContacts.detail.newCorrected !== undefined
                ? quantifier(
                    analysisResult?.importedContacts.detail.newCorrected +
                      analysisResult?.importedContacts.detail.new
                  )
                : undefined,
            state: t('IMPORT_MAIN.news'),
          },
          {
            quantity: quantifier(analysisResult?.importedContacts.detail.updated),
            state: t('IMPORT_MAIN.updated'),
          },
          {
            quantity: quantifier(analysisResult?.quantityDiscarded.quantity),
            state: t('IMPORT_MAIN.notImported'),
          },
        ] as Array<StatsProps>,
      [
        analysisResult?.detectedContacts,
        analysisResult?.importedContacts.detail.new,
        analysisResult?.importedContacts.detail.newCorrected,
        analysisResult?.importedContacts.detail.updated,
        analysisResult?.quantityDiscarded.quantity,
        t,
      ]
    );

    const stateValidation: string[] = useMemo(() => {
      return [
        t('IMPORT_MAIN.fileReading'),
        t('IMPORT_MAIN.invalidPattern'),
        t('IMPORT_MAIN.syntaxCorrection'),
        t('IMPORT_MAIN.duplicateEmails'),
        t('IMPORT_MAIN.debugSpamTraps'),
        t('IMPORT_MAIN.insecure'),
      ];
    }, [t]);

    return (
      <Flex column>
        {!showProgressCard ? (
          <Flex withGap gapSize="medium">
            <StatsCards stats={importState}>
              <Flex withGap gapSize="medium" itemAlignment="start">
                <Flex column withGap gapSize="medium" itemAlignment="start">
                  <div className="min-h-[264px] w-full bg-[#F4F5FA] p-8">
                    <div className="pb-4">
                      <Text className="text-emblueBlue-dark" fontWeight="medium">
                        {t('IMPORT_MAIN.validationHasBeen')}
                      </Text>
                    </div>
                    {stateValidation.map((state) => (
                      <Flex key={state} withGap alignment="start" gapSize="small">
                        <CheckSimple />
                        <Text>{state}</Text>
                      </Flex>
                    ))}
                  </div>
                </Flex>
                <Flex column withGap gapSize="small" itemAlignment="start">
                  <div className="min-h-[264px] w-full bg-[#F4F5FA] p-8">
                    <div className="pb-4">
                      <Text className="text-emblueBlue-dark" fontWeight="medium">
                        {t('IMPORT_MAIN.validationHasBeen')}
                      </Text>
                    </div>
                    <Flex withGap gapSize="medium">
                      <div className="flex h-full w-fit grow flex-col items-end justify-between text-center">
                        <Text>{quantifier(analysisResult?.importedContacts.detail.new)}</Text>
                        <Text>
                          {quantifier(analysisResult?.importedContacts.detail.newCorrected)}
                        </Text>
                      </div>
                      <Flex column itemAlignment="start">
                        <Text>{t('IMPORT_MAIN.news')}</Text>
                        <Text>{t('IMPORT_MAIN.newFixed')}</Text>
                      </Flex>
                    </Flex>
                  </div>
                </Flex>
                <Flex column withGap gapSize="small" itemAlignment="start">
                  <div className="min-h-[264px] w-full bg-[#F4F5FA] p-8">
                    <div className="pb-4">
                      <Text className="text-emblueBlue-dark" fontWeight="medium">
                        {t('IMPORT_MAIN.validationHasBeen')}
                      </Text>
                    </div>
                    <Flex withGap gapSize="medium">
                      <div className="flex h-full w-fit grow flex-col items-end justify-between text-center">
                        <Text>{quantifier(analysisResult?.importedContacts.detail.updated)}</Text>
                        <Text>
                          {quantifier(analysisResult?.importedContacts.detail.updatedCorrected)}
                        </Text>
                        <Text>
                          {quantifier(analysisResult?.importedContacts.detail.reactivated)}
                        </Text>
                        <Text>
                          {quantifier(analysisResult?.importedContacts.detail.reactivatedCorrected)}
                        </Text>
                        <Text>
                          {quantifier(analysisResult?.importedContacts.detail.noModifications)}
                        </Text>
                      </div>
                      <Flex column itemAlignment="start">
                        <Text>{t('IMPORT_MAIN.updated')}</Text>
                        <Text>{t('IMPORT_MAIN.updatedCorrected')}</Text>
                        <Text>{t('IMPORT_MAIN.reactivated')}</Text>
                        <Text>{t('IMPORT_MAIN.reactivatedCorrected')}</Text>
                        <Text>{t('IMPORT_MAIN.noModification')}</Text>
                      </Flex>
                    </Flex>
                  </div>
                </Flex>
                <Flex column withGap gapSize="small" itemAlignment="start">
                  <div className="min-h-[264px] w-full bg-[#F4F5FA] p-8">
                    <div className="pb-4">
                      <Text className="text-emblueBlue-dark" fontWeight="medium">
                        {t('IMPORT_MAIN.validationHasBeen')}
                      </Text>
                    </div>
                    <Flex withGap gapSize="medium">
                      <div className="flex h-full w-fit grow flex-col items-end justify-between text-center">
                        <Text>{quantifier(analysisResult?.quantityDiscarded.detail.repeated)}</Text>
                        <Text>
                          {quantifier(analysisResult?.quantityDiscarded.detail.insufficient)}
                        </Text>
                        <Text>{quantifier(analysisResult?.quantityDiscarded.detail.invalids)}</Text>
                        <Text>{quantifier(analysisResult?.quantityDiscarded.detail.garbage)}</Text>
                      </div>
                      <Flex column itemAlignment="start">
                        <Text>{t('IMPORT_MAIN.repeated')}</Text>
                        <Text>{t('IMPORT_MAIN.insufficient')}</Text>
                        <Text>{t('IMPORT_MAIN.invalid')}</Text>
                        <Text>{t('IMPORT_MAIN.garbage')}</Text>
                      </Flex>
                    </Flex>
                  </div>
                </Flex>
              </Flex>
            </StatsCards>
          </Flex>
        ) : (
          <div className="w-1/2 py-8">
            <ProgressCard progressCard={progressCard} />
          </div>
        )}
      </Flex>
    );
  }
);
